import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function PhysicalTherapy() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/physical-therapy#webpage`,
    url: `${siteUrl}/services/physical-therapy`,
    name: 'Physical Therapy at Home Las Vegas | Celebrity Home Health',
    description: 'In-home physical therapy in Las Vegas, NV — post-surgical rehab, balance training, strength building, and fall prevention. Medicare certified, ACHC accredited.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'PhysicalTherapy',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Physical Therapy', item: `${siteUrl}/services/physical-therapy` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Physical Therapy at Home Las Vegas | Celebrity Home Health';
  }, []);

  const items = [
    { icon: 'ri-walk-line', title: 'Post-Surgical Rehabilitation', desc: 'Structured recovery programs after hip/knee replacement, back surgery, and more.' },
    { icon: 'ri-body-scan-line', title: 'Balance & Gait Training', desc: 'Improve stability, coordination, and walking patterns to regain confidence.' },
    { icon: 'ri-trophy-line', title: 'Strength & Endurance', desc: 'Progressive exercises to rebuild muscle strength and cardiovascular endurance.' },
    { icon: 'ri-shield-check-line', title: 'Fall Prevention', desc: 'Home hazard assessments and targeted exercises to dramatically reduce fall risk.' },
    { icon: 'ri-heart-line', title: 'Pain Reduction', desc: 'Manual therapy, therapeutic ultrasound, and targeted techniques to relieve pain.' },
    { icon: 'ri-file-list-3-line', title: 'Home Exercise Programs', desc: 'Personalized exercise plans you can perform between therapy sessions.' },
  ];
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-physical-therapy" />
      <Header />
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/dee7a65e327d1e3c26c060a4fb7c3c3a.jpg" alt="Physical Therapy Home Care" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6"><i className="ri-walk-line"></i> Physical Therapy</div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Physical Therapy<br />In the <span className="text-amber-400">Comfort of Home</span></h1>
          <p className="text-slate-300 text-xl leading-relaxed mb-8">Regain your strength, mobility, and independence with personalized physical therapy delivered by our licensed therapists in your Las Vegas home. Medicare certified.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request PT Today</a>
            <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i> 866-939-0330</a>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">What We Provide</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Physical Therapy Services</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Comprehensive rehabilitation programs designed to restore function and improve quality of life.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item, i) => (
              <div key={i} className="bg-stone-50 p-7 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4"><i className={`${item.icon} text-amber-700 text-lg`}></i></div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Start Your Recovery Today</h2>
          <p className="text-slate-400 text-lg mb-8">Call us or submit a request and we'll match you with an experienced physical therapist in Las Vegas within 24–48 hours.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Care Now</a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">View All Services</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
